import type {
  IAuthService,
  AuthSession,
  AuthSessionTokens,
  AuthUser,
  LoginCredentials,
  PlatformAdminTenantViewAccess,
  ProSignupCredentials,
} from '../../interfaces/src';
import type { SupabaseClientOptions } from '@supabase/supabase-js';
import { getSupabaseClient } from './client';

const TOKEN_REFRESH_WINDOW_MS = 60_000;

export class SupabaseAuthService implements IAuthService {
  private supabase;
  private authSupabase;

  constructor(
    supabaseUrl: string,
    supabaseAnonKey: string,
    options?: SupabaseClientOptions<'public'>,
    private readonly externalAccessToken?: string,
  ) {
    this.supabase = getSupabaseClient(supabaseUrl, supabaseAnonKey, options);
    this.authSupabase = getSupabaseClient(supabaseUrl, supabaseAnonKey, withoutGlobalHeaders(options));
  }

  async login(credentials: LoginCredentials): Promise<AuthSession> {
    const { data, error } = await this.authSupabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password,
    });
    if (error) throw error;
    if (!data.user || !data.session) throw new Error('Login failed');

    return {
      user: {
        id: data.user.id,
        email: data.user.email!,
        role: extractRole(data.user.user_metadata),
      },
      accessToken: data.session.access_token,
    };
  }

  async signup(credentials: LoginCredentials): Promise<AuthSession | null> {
    const { data, error } = await this.authSupabase.auth.signUp({
      email: credentials.email,
      password: credentials.password,
    });
    if (error) throw error;
    // When email confirmation is enabled (Supabase default), `session` is
    // null and the user must click the link in their inbox before they
    // can log in. With confirmation disabled they're already logged in.
    if (!data.user) throw new Error('Signup failed');
    if (!data.session) return null;
    return {
      user: {
        id: data.user.id,
        email: data.user.email!,
        role: extractRole(data.user.user_metadata),
      },
      accessToken: data.session.access_token,
    };
  }

  async signupAsPro(credentials: ProSignupCredentials): Promise<AuthSession | null> {
    const { data, error } = await this.authSupabase.auth.signUp({
      email: credentials.email,
      password: credentials.password,
      options: {
        data: {
          role: 'pro_customer',
          first_name: credentials.firstName,
          last_name: credentials.lastName,
          ...(credentials.metadata ?? {}),
        },
      },
    });
    if (error) throw error;
    if (!data.user) throw new Error('Signup failed');
    if (!data.session) return null;
    return {
      user: {
        id: data.user.id,
        email: data.user.email!,
        role: extractRole(data.user.user_metadata),
      },
      accessToken: data.session.access_token,
    };
  }

  async requestPasswordReset(email: string, redirectTo?: string): Promise<void> {
    const { error } = await this.authSupabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });
    if (error) throw error;
  }

  async updatePassword(newPassword: string): Promise<void> {
    const { error } = await this.authSupabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  }

  onPasswordRecovery(callback: () => void): () => void {
    if (this.externalAccessToken) {
      return () => undefined;
    }

    const { data } = this.authSupabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') callback();
    });
    return () => data.subscription.unsubscribe();
  }

  async logout(): Promise<void> {
    const { error } = await this.authSupabase.auth.signOut();
    if (error) throw error;
  }

  async getAccessToken(): Promise<string | null> {
    const sessionTokens = await this.getSessionTokens();
    return sessionTokens?.accessToken ?? null;
  }

  async getSessionTokens(): Promise<AuthSessionTokens | null> {
    if (this.externalAccessToken) {
      return {
        accessToken: this.externalAccessToken,
        refreshToken: null,
        expiresAt: null,
      };
    }

    // Supabase will throw AuthApiError when the persisted session has a
    // refresh token that's no longer valid (deleted, expired). Treat that
    // as "not signed in" so the app shows the login screen instead of
    // surfacing a red console error on every cold start.
    try {
      const { data } = await this.authSupabase.auth.getSession();
      const session = data.session;
      if (!session) return null;
      const expiresAt = session.expires_at ?? null;
      const refreshToken = session.refresh_token;
      const shouldRefresh =
        Boolean(refreshToken) &&
        expiresAt !== null &&
        expiresAt * 1000 <= Date.now() + TOKEN_REFRESH_WINDOW_MS;
      if (!shouldRefresh) {
        return {
          accessToken: session.access_token,
          refreshToken: refreshToken ?? null,
          expiresAt,
        };
      }

      const { data: refreshed, error } = await this.authSupabase.auth.refreshSession({
        refresh_token: refreshToken,
      });
      if (error) throw error;
      const nextSession = refreshed.session ?? session;
      return {
        accessToken: nextSession.access_token,
        refreshToken: nextSession.refresh_token ?? null,
        expiresAt: nextSession.expires_at ?? null,
      };
    } catch (err) {
      if (isInvalidRefreshTokenError(err)) {
        await this.authSupabase.auth.signOut().catch(() => undefined);
        return null;
      }
      throw err;
    }
  }

  async acceptPlatformAdminTenantViewHandoff(
    token: string,
  ): Promise<PlatformAdminTenantViewAccess> {
    if (this.externalAccessToken) {
      return this.consumePlatformAdminTenantView(token);
    }

    const { data: sessionData } = await this.authSupabase.auth.getSession().catch(() => ({
      data: { session: null },
    }));
    if (sessionData.session?.access_token) {
      try {
        return await this.consumePlatformAdminTenantView(token);
      } catch {
        // Fall through to the edge SSO exchange. This covers dashboard-origin
        // opens where the local session is absent or stale while keeping the
        // same-origin admin flow off URL-borne JWTs.
      }
    }

    const { data, error } = await this.supabase.functions.invoke('platform-admin-handoff', {
      body: { token },
    });
    if (error) throw error;

    const payload = asRecord(data);
    const email = readString(payload.email);
    const tokenHash = readString(payload.tokenHash);
    const tenantId = readString(payload.tenantId);
    const expiresAt = readString(payload.expiresAt);
    const viewExpiresAt = readString(payload.viewExpiresAt);

    if (!email || !tokenHash || !tenantId || !expiresAt || !viewExpiresAt) {
      throw new Error('Invalid admin handoff response.');
    }

    const { error: verifyError } = await this.authSupabase.auth.verifyOtp({
      type: 'magiclink',
      email,
      token_hash: tokenHash,
    });
    if (verifyError) throw verifyError;

    return { tenantId, expiresAt, viewExpiresAt };
  }

  private async consumePlatformAdminTenantView(
    token: string,
  ): Promise<PlatformAdminTenantViewAccess> {
    const { data, error } = await this.supabase.rpc('platform_admin_consume_tenant_view', {
      p_token: token,
    });
    if (error) throw error;
    return mapTenantViewAccess(data);
  }

  async getCurrentUser(): Promise<AuthUser | null> {
    try {
      if (this.externalAccessToken) {
        const { data, error } = await this.authSupabase.auth.getUser(this.externalAccessToken);
        if (error) throw error;
        if (!data.user) return null;
        return {
          id: data.user.id,
          email: data.user.email!,
          role: extractRole(data.user.user_metadata),
        };
      }

      const { data, error } = await this.authSupabase.auth.getUser();
      // getUser() returns an error object (rather than throwing) when the
      // refresh token is missing/invalid. Same treatment: clear and bail.
      if (error && isInvalidRefreshTokenError(error)) {
        await this.authSupabase.auth.signOut().catch(() => undefined);
        return null;
      }
      if (!data.user) return null;
      return {
        id: data.user.id,
        email: data.user.email!,
        role: extractRole(data.user.user_metadata),
      };
    } catch (err) {
      if (isInvalidRefreshTokenError(err)) {
        await this.authSupabase.auth.signOut().catch(() => undefined);
        return null;
      }
      throw err;
    }
  }

  onAuthStateChange(callback: (user: AuthUser | null) => void): () => void {
    if (this.externalAccessToken) {
      return () => undefined;
    }

    const { data } = this.authSupabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        callback({
          id: session.user.id,
          email: session.user.email!,
          role: extractRole(session.user.user_metadata),
        });
      } else {
        callback(null);
      }
    });
    return () => data.subscription.unsubscribe();
  }
}

function withoutGlobalHeaders(
  options: SupabaseClientOptions<'public'> | undefined,
): SupabaseClientOptions<'public'> | undefined {
  if (!options?.global?.headers) return options;
  const { global, ...rest } = options;
  const { headers: _headers, ...globalRest } = global;
  return Object.keys(globalRest).length > 0
    ? { ...rest, global: globalRest }
    : rest;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function mapTenantViewAccess(value: unknown): PlatformAdminTenantViewAccess {
  const payload = asRecord(value);
  const tenantId = readString(payload.tenantId);
  const expiresAt = readString(payload.expiresAt);
  const viewExpiresAt = readString(payload.viewExpiresAt);

  if (!tenantId || !expiresAt || !viewExpiresAt) {
    throw new Error('Invalid admin handoff response.');
  }

  return { tenantId, expiresAt, viewExpiresAt };
}

function extractRole(meta: Record<string, unknown> | null | undefined): string | null {
  if (!meta) return null;
  const r = meta['role'];
  return typeof r === 'string' ? r : null;
}

/** Recognises the family of errors Supabase raises when the persisted refresh
 *  token can no longer be used (deleted server-side, expired, or never
 *  existed). All variants share the "Invalid Refresh Token" / "refresh_token"
 *  signature in either `name`, `code`, or `message`. */
function isInvalidRefreshTokenError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { name?: string; code?: string; message?: string; status?: number };
  const name = e.name ?? '';
  const code = e.code ?? '';
  const message = e.message ?? '';
  if (name === 'AuthSessionMissingError') return true;
  if (code === 'refresh_token_not_found' || code === 'invalid_refresh_token') return true;
  if (/refresh\s*token/i.test(message) && /(invalid|not\s*found|missing)/i.test(message)) return true;
  return false;
}
